"use client";
import Image from "next/image";
import Link from "next/link";

interface Hero2Props {
    data: {
        id: string;
        title: string;
        description: string;
        picture?: any;
        bgImage?: any;
        button?: any[];
        isLive: boolean;
    };
}

export default function Hero2_Layout2({ data }: Hero2Props) {
    if (!data?.isLive) return null;

    const leftImg = data?.picture?.data?.attributes?.url;
    const rightImg = data?.bgImage?.data?.attributes?.url;

    return (
        <section className="w-full min-h-[70vh] bg-gradient-to-br from-[#FFF9F9] via-white to-[#FDF8F8] py-10 md:py-14 lg:py-16 relative overflow-hidden">
            {/* Subtle Background Pattern - Smaller */}
            <div className="absolute inset-0 opacity-[0.02] z-0">
                <div className="absolute top-8 left-8 w-40 h-40 bg-gradient-to-r from-gold-100 to-transparent rounded-full blur-2xl"></div>
                <div className="absolute bottom-8 right-8 w-48 h-48 bg-gradient-to-l from-gold-50 to-transparent rounded-full blur-2xl"></div>
            </div>

            <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
                {/* Main Grid Container - Reduced gap */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-start">
                    
                    {/* LEFT SECTION - Compact Title + Image */}
                    <div className="space-y-6 md:space-y-8">
                        {/* Title - More compact */}
                        <div className="relative">
                            {/* Decorative Accent Line - Smaller */}
                            <div className="hidden lg:block absolute -left-4 top-0 w-1 h-16 bg-gradient-to-b from-[#D4AF37] via-[#F4D03F] to-transparent rounded-full"></div>
                            
                            {/* Main Title - Smaller font sizes */}
                            <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl   font-light text-gray-900 leading-snug md:leading-snug lg:leading-tight tracking-tight">
                                {data?.title || "Where Craftsmanship Meets Timelessness"}
                            </h2>
                            
                            {/* Subtle Underline Effect - Smaller */}
                            <div className="mt-4 w-16 h-0.5 bg-gradient-to-r from-[#D4AF37]/40 to-transparent"></div>
                        </div>

                        {/* Featured Image - Smaller dimensions */}
                        {leftImg && (
                            <div className="relative mt-6 lg:mt-8 group">
                                <div className="relative w-full h-[280px] md:h-[320px] lg:h-[360px] xl:h-[400px] overflow-hidden rounded-xl shadow-lg transition-all duration-500 group-hover:shadow-xl">
                                    <Image
                                        src={leftImg}
                                        alt="Jewelry"
                                        fill
                                        className="object-cover object-center transition-transform duration-500 group-hover:scale-105"
                                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 600px"
                                        priority
                                    />
                                    
                                    {/* Simplified Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-transparent"></div>
                                </div>
                                
                                {/* Smaller Decorative Corners */}
                                <div className="absolute -top-2 -left-2 w-4 h-4 border-t border-l border-[#D4AF37] rounded-tl opacity-30"></div>
                                <div className="absolute -bottom-2 -right-2 w-4 h-4 border-b border-r border-[#D4AF37] rounded-br opacity-30"></div>
                            </div>
                        )}
                    </div>

                    {/* RIGHT SECTION - Compact Layout */}
                    <div className="relative space-y-6 md:space-y-8 lg:space-y-10">
                        {/* Image - Smaller */}
                        {rightImg && (
                            <div className="relative group">
                                <div className="relative w-full h-[240px] md:h-[280px] lg:h-[320px] xl:h-[360px] overflow-hidden rounded-xl shadow-md transition-all duration-300 group-hover:shadow-lg">
                                    <Image
                                        src={rightImg}
                                        alt="Model"
                                        fill
                                        className="object-cover object-center transition-transform duration-500 group-hover:scale-105"
                                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 600px"
                                    />
                                    
                                    {/* Simple Gradient Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
                                </div>
                                
                                {/* Smaller Caption */}
                                <div className="absolute bottom-4 left-4 text-white text-xs font-light opacity-70">
                                    Timeless Elegance
                                </div>
                            </div>
                        )}
                        
                        {/* Content Section - More compact */}
                        <div className="space-y-4 md:space-y-6">
                            {/* Description - Smaller text */}
                            <div className="relative">
                                <p className="text-base md:text-lg text-gray-700 leading-relaxed md:leading-relaxed font-light max-w-none lg:max-w-[95%]">
                                    {data?.description ||
                                        "Our jewellery designs are timeless and lovingly crafted to merge classic artistry with modern elegance. With each wear, they become uniquely yours."
                                    }
                                </p>
                                
                                {/* Smaller Quote Mark */}
                                <div className="hidden lg:block absolute -left-6 top-0 text-4xl text-[#D4AF37]/15  ">"</div>
                            </div>
                            
                            {/* Compact CTA Button */}
                            {data?.button?.[0] && (
                                <div className="pt-2 md:pt-4">
                                    <Link
                                        href={data.button[0].url || "#"}
                                        className="inline-flex items-center gap-2 px-6 md:px-7 py-2.5 md:py-3 bg-black text-white font-medium rounded-full hover:shadow-lg transition-all duration-300 hover:scale-[1.02] text-sm md:text-base group relative overflow-hidden"
                                    >
                                        {/* Button Content */}
                                        <span className="relative tracking-wide font-normal">
                                            {data.button[0].text || "Discover More"}
                                        </span>
                                        <span className="relative transform transition-transform duration-300 group-hover:translate-x-1">
                                            →
                                        </span>
                                    </Link>
                                    
                                    {/* Optional Subtext - Smaller */}
                                    {/* <p className="mt-2 text-xs text-gray-500 font-light">
                                        Handcrafted with passion
                                    </p> */}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Compact Bottom Border */}
                {/* <div className="mt-12 pt-6 border-t border-gray-100">
                    <div className="flex items-center justify-center space-x-4 text-xs text-gray-400 tracking-wider">
                        <span>•</span>
                        <span className="text-[10px] md:text-xs">ETHICAL</span>
                        <span>•</span>
                        <span className="text-[10px] md:text-xs">HANDCRAFTED</span>
                        <span>•</span>
                        <span className="text-[10px] md:text-xs">WARRANTY</span>
                        <span>•</span>
                    </div>
                </div> */}
            </div>
        </section>
    );
}